import {
  Condition,
  DepositPayable,
  FormCondition,
  LinkedDateImpact,
  StaffPreviewLinkedDatesRequestSourceDateType,
  useInvalidateUseStaffGetContractDates,
  useStaffPreviewLinkedDates,
} from '@urbanx/agx.campaigns.client';
import {
  AgxBadge,
  AgxDatePicker,
  AgxDivider,
  AgxSpacer,
  AgxTypography,
  formatDate,
  toDateOnlyString,
} from '@urbanx/agx.ui-components2';
import { useEffect, useRef, useState } from 'react';
import { LinkedDateWarningModal } from '../../modals/LinkedDateWarningModal';
import { useFormConditionDueDate } from '../../components/useFormConditionDueDate';
import '../ConditionDatePicker.scss';

interface SpecialConditionClauseProps {
  itemId: string;
  label: string;
  dueDate: string | undefined;
  conditionId: string;
  contractId?: string;
  campaignId?: string;
  isLastItem: boolean;
  dueDateCondition: FormCondition | undefined;
  onDueDateUpdated: (
    conditionId: string,
    clauseId: string,
    newDueDateCondition: FormCondition | undefined,
    newDueDate: string | undefined
  ) => void | Promise<void>;
}

interface PendingDueDateChange {
  requestVersion: number;
  condition: FormCondition;
  dueDate: string | undefined;
}

const getInitialDueDateCondition = (
  dueDateCondition: FormCondition | undefined,
  renderedDueDate: string | undefined
): FormCondition => {
  if (dueDateCondition?.condition) {
    return {
      ...dueDateCondition,
      depositPayable:
        dueDateCondition.depositPayable ??
        (dueDateCondition.condition === Condition.OnSpecificDate
          ? DepositPayable.On
          : DepositPayable.NDaysFrom),
    };
  }

  if (dueDateCondition?.referenceDate) {
    return {
      ...dueDateCondition,
      condition: Condition.OnSpecificDate,
      depositPayable: DepositPayable.On,
    };
  }

  if (renderedDueDate) {
    return {
      condition: Condition.OnSpecificDate,
      depositPayable: DepositPayable.On,
      referenceDate: renderedDueDate,
    };
  }

  return {};
};

const getDueDateBadgeText = (dueDate: Date | null) => {
  if (!dueDate) return undefined;

  return formatDate(dueDate.toISOString());
};

const SpecialConditionClause = ({
  itemId,
  label,
  dueDate: renderedDueDate,
  conditionId,
  contractId,
  campaignId,
  isLastItem,
  dueDateCondition,
  onDueDateUpdated,
}: SpecialConditionClauseProps) => {
  const [linkedDateImpacts, setLinkedDateImpacts] = useState<LinkedDateImpact[]>([]);
  const [pendingDueDateChange, setPendingDueDateChange] = useState<PendingDueDateChange>();
  const dueDateRequestVersion = useRef(0);

  const [localDueDateCondition, setLocalDueDateCondition] = useState<FormCondition>(() =>
    getInitialDueDateCondition(dueDateCondition, renderedDueDate)
  );
  const calculatedDueDate = useFormConditionDueDate(localDueDateCondition);
  const dueDateBadgeText = getDueDateBadgeText(calculatedDueDate.dueDate);

  useEffect(() => {
    dueDateRequestVersion.current += 1;
    setLinkedDateImpacts([]);
    setPendingDueDateChange(undefined);
    setLocalDueDateCondition(getInitialDueDateCondition(dueDateCondition, renderedDueDate));
  }, [dueDateCondition, renderedDueDate]);

  const invalidateUseStaffGetContractDates = useInvalidateUseStaffGetContractDates();
  const { staffPreviewLinkedDates } = useStaffPreviewLinkedDates({
    onSuccess: () => {},
    onError: () => {},
  });

  const saveDueDate = async (
    newDueDateCondition: FormCondition,
    newDueDate: string | undefined
  ) => {
    await onDueDateUpdated(
      conditionId,
      itemId,
      newDueDateCondition.condition ? newDueDateCondition : undefined,
      newDueDate
    );
    if (campaignId && contractId) {
      await invalidateUseStaffGetContractDates(campaignId, contractId);
    }
  };

  const onDueDateChanged = async (value: Date | null) => {
    if (!itemId || !conditionId || !value) return;

    dueDateRequestVersion.current += 1;
    const requestVersion = dueDateRequestVersion.current;

    const nextDueDate = toDateOnlyString(value);
    const newDueDateCondition = nextDueDate
      ? {
          condition: Condition.OnSpecificDate,
          referenceDate: nextDueDate,
          completedDate: localDueDateCondition.completedDate,
        }
      : {};

    setLocalDueDateCondition(newDueDateCondition);
    setLinkedDateImpacts([]);
    setPendingDueDateChange(undefined);

    try {
      if (!campaignId || !contractId) {
        await saveDueDate(newDueDateCondition, nextDueDate);
        return;
      }

      const preview = await staffPreviewLinkedDates({
        campaignId,
        contractId,
        body: {
          sourceDateType: StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate,
          proposedDate: nextDueDate,
          specialConditionClauseId: itemId,
        },
      });

      if (requestVersion !== dueDateRequestVersion.current) return;

      const impactedDates = preview?.impactedDates ?? [];
      if (impactedDates.length > 0) {
        setLinkedDateImpacts(impactedDates);
        setPendingDueDateChange({
          requestVersion,
          condition: newDueDateCondition,
          dueDate: nextDueDate,
        });
        return;
      }

      await saveDueDate(newDueDateCondition, nextDueDate);
    } catch {
      if (requestVersion !== dueDateRequestVersion.current) return;
      setLocalDueDateCondition(getInitialDueDateCondition(dueDateCondition, renderedDueDate));
    }
  };

  const onCancelLinkedDateWarning = () => {
    dueDateRequestVersion.current += 1;
    setLinkedDateImpacts([]);
    setPendingDueDateChange(undefined);
    setLocalDueDateCondition(getInitialDueDateCondition(dueDateCondition, renderedDueDate));
  };

  const onConfirmLinkedDateWarning = () => {
    if (
      !pendingDueDateChange ||
      pendingDueDateChange.requestVersion !== dueDateRequestVersion.current
    )
      return;

    setLinkedDateImpacts([]);
    setPendingDueDateChange(undefined);
    void saveDueDate(pendingDueDateChange.condition, pendingDueDateChange.dueDate);
  };

  return (
    <>
      <AgxSpacer
        className="special-condition-clause-content"
        fill
        direction="vertical"
        gap="spacing-xl"
      >
        <AgxTypography
          type="body_ui"
          size="small"
          id={`special-condition-date-${itemId}`}
          color="var(--text-secondary)"
        >
          {label}
        </AgxTypography>
        {dueDateBadgeText && (
          <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
            <AgxTypography
              id={`special-condition-date-${itemId}-due-on`}
              type="body_ui"
              size="small"
              color="var(--text-primary)"
            >
              Due on
            </AgxTypography>
            <AgxBadge type="colourfill" colour="grey" size="md" text={dueDateBadgeText} />
          </AgxSpacer>
        )}
        <AgxDatePicker
          id={`special-condition-date-${itemId}-date`}
          className="contract-information-date-picker"
          label="Due Date"
          required
          optionalText=""
          value={calculatedDueDate.dueDate}
          onChange={onDueDateChanged}
        />
      </AgxSpacer>
      {!isLastItem && <AgxDivider variant={'horizontal'} />}
      {linkedDateImpacts.length > 0 && (
        <LinkedDateWarningModal
          impactedDates={linkedDateImpacts}
          sourceDateTypes={[StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate]}
          onCancel={onCancelLinkedDateWarning}
          onConfirm={onConfirmLinkedDateWarning}
        />
      )}
    </>
  );
};

export default SpecialConditionClause;
